import { FinancialFlowTypeEnum } from '@zaoblako/shared/modules/catalog/enums/financial-flow-type.enum';
import { DirectionKindEnum } from '@zaoblako/shared/modules/catalog/enums/direction.kind.enum';
import { DirectionTypeEnum } from '@zaoblako/shared/modules/catalog/enums/direction.type.enum';
import { AccountingAccountFindDto, type AccountingAccountFindInterface } from '@zaoblako/shared/modules/catalog/dtos/accounting-account/accounting-account-find.dto';
import { CompanyGroupFindDto, type CompanyGroupFindInterface } from '@zaoblako/shared/modules/client/dtos/company.group/company-group-find.dto';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { UserGroupFindDto, type UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { ClassificationCategoryFindDto, type ClassificationCategoryFindInterface } from '@zaoblako/shared/modules/finance/dtos/classification/classification-category-find.dto';
import {
  DirectionBalanceSheetSettingsFindDto,
  type DirectionBalanceSheetSettingsFindInterface,
} from '@zaoblako/shared/modules/catalog/dtos/direction/direction-balance-sheet-settings-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { CatalogDtosKeysEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-dtos-keys.enum';
import { BalanceSheetSourceEnum } from '@zaoblako/shared/modules/catalog/enums/balance-sheet-source.enum';

import * as yup from 'yup';

/** Direction response body */
export interface DirectionFindInterface {
  /** Unique identifier */
  id: number;
  /** Display name */
  name: string;
  /** Is direction group */
  group: boolean;
  /** Financial flow direction belongs to */
  financialFlow?: FinancialFlowTypeEnum;
  /** Direction kind */
  kind?: DirectionKindEnum;
  /** Is soft deleted */
  deleted: boolean;
  /** Custom user sort index */
  sortIndex?: number;
  /** Identifier of direction unit in Atrium CRM */
  atriumId?: number;
  /** Special code of this direction for Atrium CRM (using by some reports) */
  atriumCode?: string;
  /** Is direction exported to Atrium CRM */
  exportedToAtrium?: boolean;
  /** Direction color (using by some reports) */
  color?: string;
  /** Accounting account number (using by some reports) */
  accountingAccount?: AccountingAccountFindInterface;
  /** Balance sheet source (using by some reports) */
  balanceSheetSource?: BalanceSheetSourceEnum;
  /** Flag which marks that accounting settings are set for direction */
  hasAccountingSettings?: boolean;
  /** Direction type */
  type?: DirectionTypeEnum;
  /** Parent direction */
  parent?: DirectionFindInterface;
  /** Child directions */
  children?: DirectionFindInterface[];
  /** Related company groups */
  companyGroups?: CompanyGroupFindInterface[];
  /** Generatl direction (for plans) */
  generalDirection?: DirectionFindInterface;
  /** Users whose has access to direction in budget requests */
  users?: UserFindInterface[];
  /** User groups that have access to this direction */
  userGroups?: UserGroupFindInterface[];
  /** Financial classification categories for this direction */
  classification?: ClassificationCategoryFindInterface[];
  /** Balance sheet settings for direction (dynamic field) */
  balanceSheetSettings?: DirectionBalanceSheetSettingsFindInterface[];
}

/** Direction response body - DTO schema */
export const DirectionFindDto = SerdeDtoClass.create<DirectionFindInterface>(CatalogDtosKeysEnum.DIRECTION_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  name: {
    sortable: true,
    label: 'Название',
    serializer: yup.string(),
  },
  group: {
    label: 'Группа',
    serializer: yup.boolean(),
  },
  financialFlow: {
    optional: true,
    label: 'Финансовый поток',
    hint: 'Тип финансового потока, к которому относится БС. Вычисляется автоматически по виду БС. Бывает только "Доходом" или "Расходом".',
    serializer: yup
      .string()
      .oneOf(Object.values(FinancialFlowTypeEnum)),
  },
  kind: {
    optional: true,
    label: 'Вид',
    serializer: yup
      .string()
      .oneOf(Object.values(DirectionKindEnum)),
  },
  deleted: {
    label: 'Удалена',
    serializer: yup.boolean(),
  },
  sortIndex: {
    sortable: true,
    optional: true,
    label: 'Пользовательский индекс сортировки',
    hint: 'Уникален в рамках платежного аккаунта (для "корней") или родителя (для "листьев")',
    serializer: yup.number(),
  },
  atriumId: {
    label: 'Номер в "Атриум CRM"',
    optional: true,
    serializer: yup.number(),
  },
  atriumCode: {
    label: 'Код для "Атриума"',
    optional: true,
    serializer: yup.string(),
  },
  exportedToAtrium: {
    label: 'Выгружена в "Атриум CRM"',
    optional: true,
    serializer: yup.boolean(),
  },
  color: {
    optional: true,
    label: 'Цвет',
    serializer: yup.string(),
  },
  accountingAccount: {
    optional: true,
    label: 'Бухгалтерский счет',
    nested: AccountingAccountFindDto,
    serializer: (_, nested) => nested,
  },
  balanceSheetSource: {
    optional: true,
    label: 'Источник',
    serializer: yup
      .string()
      .oneOf(Object.values(BalanceSheetSourceEnum)),
  },
  hasAccountingSettings: {
    label: 'Настроен бухгалтерский счет',
    optional: true,
    serializer: yup.boolean(),
  },
  type: {
    optional: true,
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(DirectionTypeEnum)),
  },
  parent: {
    label: 'Группа',
    optional: true,
    nested: CatalogDtosKeysEnum.DIRECTION_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  children: {
    label: 'Потомки',
    optional: true,
    nested: CatalogDtosKeysEnum.DIRECTION_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
  companyGroups: {
    label: 'Группы компаний',
    optional: true,
    nested: CompanyGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  generalDirection: {
    label: 'Рассчетная БС',
    optional: true,
    nested: CatalogDtosKeysEnum.DIRECTION_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  users: {
    label: 'Пользователи',
    optional: true,
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  userGroups: {
    label: 'Группы пользователей',
    optional: true,
    nested: UserGroupFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  classification: {
    optional: true,
    label: 'Финансовая классификация',
    nested: ClassificationCategoryFindDto,
    serializer: (_, nested): yup.Schema<any> => yup.array(nested),
  },
  balanceSheetSettings: {
    optional: true,
    label: 'Настройки для ОСВ',
    nested: DirectionBalanceSheetSettingsFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
});
